import { Fragment, useEffect, useState } from "react";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Checkbox from "@mui/material/Checkbox";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { toCamelCase } from "../../helpers/appUtils";
import AlertBar from "../utils/AlertBar";
import { API_URL } from "../../config/environment";
import secureLocalStorage from "react-secure-storage";
import {
  hasAdminOrEditorAccess,
  hasSubscriberAccess,
} from "../../helpers/roles";

const SelectInput = ({
  label,
  getParams,
  initialValue,
  technofunctionality,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [value, setValue] = useState(initialValue || []);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const loading = open && options.length === 0;
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const storedUser = secureLocalStorage.getItem("userInfo");
  const user = JSON.parse(storedUser);

  const labels = {
    Technofunctionality: t("dashboard.ingredientSelection.input1"),
    "Source Category": t("dashboard.ingredientSelection.input2"),
    Sector: t("dashboard.ingredientSelection.input3"),
    "Product Platform": t("dashboard.ingredientSelection.input4"),
  };

  function allowMultipleSelect(label) {
    if (label === "Technofunctionality") {
      return hasAdminOrEditorAccess(user) || hasSubscriberAccess(user);
    } else {
      return true;
    }
  }

  async function getCategories() {
    let url = "";
    const categoryType = toCamelCase(label);

    if (label === "Technofunctionality") {
      url = `${API_URL}/categories?categoryType=${categoryType}`;
    } else {
      const _technofunctionality = Array.isArray(technofunctionality)
        ? technofunctionality?.join(";")
        : technofunctionality;
      url = `${API_URL}/categories/techno-functionality?categoryType=${categoryType}&technofunctionalities=${_technofunctionality}`;
    }

    const response = await fetch(url);
    const data = await response.json();

    if (data.status === "success") {
      const categories = data.categories;
      return categories;
    } else {
      console.log("Could not fetch data from server");
      setIsError(true);
      setErrorMessage(data.message);
    }
  }

  function handleIsErrorState() {
    setIsError(false);
  }

  useEffect(() => {
    getParams(value, label);
  }, [value]);

  useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      const categories = await getCategories();
      if (active && categories) {
        setOptions([...categories]);
        const _categories = JSON.stringify(categories);
        secureLocalStorage.setItem("categories", _categories);
      }
    })();

    // TODO: Implement a cleanup to abort requests using AbortController
    return () => {
      active = false;
    };
  }, [loading]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  return (
    <>
      <Autocomplete
        limitTags={2}
        size="small"
        multiple={allowMultipleSelect(label)}
        id="select-input"
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        onChange={(e, newValue) => {
          setValue(newValue);
        }}
        isOptionEqualToValue={(option, value) => option === value}
        options={options}
        loading={loading}
        getOptionLabel={(option) => option}
        disableCloseOnSelect
        style={{ maxHeight: "200px" }} // Set your preferred maximum height here
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: "5px", height: "20px" }}
              checked={selected}
            />
            {option}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={labels[label]}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <Fragment>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </Fragment>
              ),
            }}
          />
        )}
        value={value}
      />

      <AlertBar
        open={isError}
        message={errorMessage}
        severity={"error"}
        onClose={handleIsErrorState}
      />
    </>
  );
};

export default SelectInput;
